import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import StreamCard from "@/components/StreamCard";
import { TIDS } from "@/constants/testIds";
import { Search, Radio } from "lucide-react";

// Category tabs. `match` accepts an array of channel `category` strings that
// map to this tab. Tabs are ordered by expected popularity.
const CATEGORIES = [
  { id: "all",     label: "All",              matchFn: null },
  { id: "gaming",  label: "Gaming",
    matchFn: (c) => !["Music", "Just Chatting", "Crypto Gambling", "Slots", "Casino Games", "Sports Betting"].includes(c) },
  { id: "casino",  label: "Casino & Slots",
    matchFn: (c) => ["Crypto Gambling", "Slots", "Casino Games", "Sports Betting"].includes(c) },
  { id: "music",   label: "Music",            matchFn: (c) => c === "Music" },
  { id: "chatting",label: "Just Chatting",    matchFn: (c) => c === "Just Chatting" },
];

export default function Browse() {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [query, setQuery] = useState("");

  useEffect(() => {
    // live_only=true — the browse front page shows only currently-live streams.
    api.get("/channels?live_only=true")
      .then(r => setChannels(r.data))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const activeTab = CATEGORIES.find(c => c.id === tab);
    let list = channels;
    if (activeTab?.matchFn) list = list.filter(c => activeTab.matchFn(c.category || ""));
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter(c =>
        (c.title || "").toLowerCase().includes(q) ||
        (c.username || "").toLowerCase().includes(q) ||
        (c.category || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [channels, tab, query]);

  // Category counts (all vs current filter) — computed off `channels` regardless
  // of the search query so tab labels don't dance around while typing.
  const tabCounts = useMemo(() => {
    const counts = { all: channels.length };
    for (const c of CATEGORIES) {
      if (c.id === "all") continue;
      counts[c.id] = channels.filter(x => c.matchFn && c.matchFn(x.category || "")).length;
    }
    return counts;
  }, [channels]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <div className="sc-label mb-3">Discover</div>
      <div className="flex items-baseline gap-3 mb-6 flex-wrap">
        <h1 className="sc-heading text-4xl sm:text-5xl font-black tracking-tighter">Live right now</h1>
        <div className="flex items-center gap-2 chip-neon pink">
          <span className="live-dot" />
          <span className="sc-mono text-[11px]" data-testid="browse-live-count">{channels.length} LIVE</span>
        </div>
      </div>

      {/* Category tabs */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="browse-category-tabs" role="tablist">
        {CATEGORIES.map(c => (
          <button
            key={c.id}
            role="tab"
            aria-selected={tab === c.id}
            data-testid={`browse-tab-${c.id}`}
            onClick={() => setTab(c.id)}
            className={`px-3.5 py-1.5 text-sm rounded-sm border transition ${
              tab === c.id
                ? "border-[color:var(--sc-cyan)] bg-[color:var(--sc-cyan)]/15 text-white"
                : "border-white/10 text-zinc-400 hover:border-white/25 hover:text-zinc-200"
            }`}
          >
            {c.label}
            <span className="ml-2 sc-mono text-[10px] text-zinc-500">{tabCounts[c.id] ?? 0}</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-6 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
        <input
          type="search"
          data-testid="browse-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search streams by title, streamer, or category…"
          className="sc-input w-full !pl-9 text-sm"
          aria-label="Search streams"
        />
      </div>

      {loading ? (
        <div className="text-zinc-400" data-testid="browse-loading">Loading live streams...</div>
      ) : channels.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-md p-10 text-center" data-testid="browse-empty-live">
          <Radio className="w-8 h-8 text-zinc-600 mx-auto mb-3" />
          <div className="sc-heading text-xl font-bold">No one is live right now</div>
          <div className="text-sm text-zinc-400 mt-2">Check back soon — or become a streamer yourself.</div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-md p-10 text-center" data-testid="browse-empty-filter">
          <div className="sc-heading text-xl font-bold">No streams match</div>
          <div className="text-sm text-zinc-400 mt-2">
            Try a different category{query ? " or clear your search." : "."}
          </div>
        </div>
      ) : (
        <div data-testid={TIDS.browseGrid} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 stagger">
          {filtered.map((c, i) => <StreamCard key={c.id} channel={c} index={i} />)}
        </div>
      )}
    </div>
  );
}
